import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "../data/content";
import { Logo } from "./Logo";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="primary-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? "glass border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-[auto_1fr_auto] items-center h-[72px] lg:flex lg:justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0 justify-self-start">
          <Logo className="h-11 w-11 shadow-pink" />
          <span className="hidden lg:flex flex-col leading-tight">
            <span className="font-display text-black text-xl tracking-tight">Creative Flare</span>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#EC4899]">Yorkshire · C.I.C.</span>
          </span>
        </Link>

        <span className="lg:hidden flex flex-col items-center justify-center leading-tight text-center min-w-0 px-2">
          <span className="font-display text-black text-[17px] sm:text-xl tracking-tight">Creative Flare</span>
          <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.25em] uppercase text-[#EC4899]">
            Yorkshire · C.I.C.
          </span>
        </span>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-[14px] tracking-wide transition-colors ${
                  isActive ? "text-black" : "text-black/65 hover:text-black"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#EC4899] rounded-full" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0 justify-self-end">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full text-[13px] font-medium bg-[#EC4899] text-white hover:bg-[#DB2777] transition-all hover:-translate-y-[1px] shadow-pink"
          >
            Get in touch
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full hover:bg-black/5"
            aria-label="Toggle navigation"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="font-display text-3xl text-black"
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-cta-contact" className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#EC4899] text-white font-medium">
              Get in touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
